import React, { useEffect, useRef } from 'react'
import WebFont from 'webfontloader'

/**
 * First-party replacement for the tiny `@dr-kobros/react-webfont-loader` npm
 * package (a thin React wrapper around Typekit's `webfontloader`). Onboarded
 * here so we no longer depend on the unmaintained external wrapper — see #19833.
 *
 * On mount, and whenever the `config` prop's reference changes, it calls
 * `WebFont.load(config)`, reports load status via `onStatus`, and delegates to
 * any `loading`/`active`/`inactive` callbacks the caller supplied on `config`.
 * Renders `children` (or `null`).
 */

export type WebfontStatus = 'loading' | 'active' | 'inactive'

/**
 * Subset of the `webfontloader` config we type explicitly. `webfontloader`
 * ships no types, so the runtime import resolves to `any`; this keeps the
 * public prop typed while still allowing any valid webfontloader option (it
 * supports many providers — google, custom, typekit, fontdeck, monotype, …).
 */
export interface WebfontConfig {
  google?: { families: string[]; text?: string }
  custom?: { families: string[]; urls?: string[] }
  typekit?: { id: string }
  timeout?: number
  classes?: boolean
  events?: boolean
  /** Document/window context, e.g. when loading into an iframe. */
  context?: Window
  /**
   * Raw webfontloader status callbacks. These are delegated to (called in
   * addition to `onStatus`) rather than overwritten, so they remain valid.
   */
  loading?: () => void
  active?: () => void
  inactive?: () => void
  [key: string]: unknown
}

export interface WebfontLoaderProps {
  /**
   * webfontloader configuration object (required).
   *
   * NOTE: fonts are (re)loaded whenever this prop's *reference* changes. Pass a
   * stable object — hoist a module-level constant, or wrap in `useMemo` — since
   * an inline object literal created on every render would re-trigger a load
   * each render.
   */
  config: WebfontConfig
  children?: React.ReactNode
  /** Called whenever the overall font-loading status changes. */
  onStatus?: (status: WebfontStatus) => void
}

export function WebfontLoader({ config, children, onStatus }: WebfontLoaderProps) {
  // Keep the latest onStatus without making it a load trigger — fonts should
  // only (re)load when the `config` prop reference changes.
  const onStatusRef = useRef(onStatus)
  onStatusRef.current = onStatus

  // Load fonts on mount and whenever the config reference changes (matches the
  // original wrapper's componentDidMount + componentDidUpdate reload behavior).
  useEffect(() => {
    const report = (status: WebfontStatus, delegate?: () => void) => {
      onStatusRef.current?.(status)
      delegate?.()
    }

    try {
      WebFont.load({
        ...config,
        loading: () => report('loading', config.loading),
        active: () => report('active', config.active),
        inactive: () => report('inactive', config.inactive)
      })
    } catch (err) {
      // WebFont.load can throw synchronously on a malformed config; don't let it
      // bubble as an uncaught effect error — fonts simply won't load.
      console.error('WebfontLoader: failed to load fonts', err)
    }
  }, [config])

  return <>{children ?? null}</>
}

export default WebfontLoader
